import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button,
  DropdownInputControlled,
  TextInputControlled,
  ToggleInput,
  TextInput,
  ToggleLayout
} from '../input';
import { TeamSettingsPageState } from '../../pages/teamSettingsPage/teamSettingsPageState';
import { UploadAvatarInput } from '../uploadTeamAvatar';
import { TeamDetailsDrawer } from '../teams/teamDetailsDrawer';
import { TeamMetadataList } from '../teams/teamMetadataList';
import { TeamEnrichmentLevelList } from '../teams/teamEnrichmentLevelList';
import { PublicTeamForm } from '../teams/publicTeamForm';
import { AdsSettingsSection } from '../teams/adsSettingsSection';
import { EmbedParamsInfoPopup } from '../../pages/teamSettingsPage/embedParamsInfoPopup';
import { TeamDictionary } from '../teams/teamDictionary';
import { GenerativeAiForm } from '../teams/generativeAiForm';
import { CustomSpecialtyDrawer } from '../customSpecialtyDrawer/customSpecialtyDrawer';
import { parseCustomSpeciality } from '../../entities/job/jobSpeciality';

type Props = {
  model: TeamSettingsPageState;
};

export const TeamEditForm = observer(({ model }: Props) => {
  return (
    <section className="teams-settings-form form">
      <div className="flex-left form-header">
        <UploadAvatarInput model={model} />

        <TextInputControlled
          model={model.teamName}
          label="Library Name"
          className="form-input team-name"
        />
      </div>

      <TextInputControlled
        model={model.teamDescription}
        label="Library Description"
        className="form-input team-description"
        showFeedback={false}
        rows={5}
      />

      <DropdownInputControlled
        model={model.teamIndustry}
        label="Field Of Activity"
        className="form-input team-industry"
        notDeselectable={true}
        showFeedback={false}
      />

      <ToggleInput
        className="medium"
        label={'Library Visibility'}
        toggled={!!model?.team?.isPublic}
        toggleLayout={ToggleLayout.Buttons}
        toggledLabel='Shareable'
        notToggledLabel='Private'
        name={'isPublic'}
        onChange={() => model.makeTeamPublic()}
        disabled={!!model?.team?.isPublic}
      />

      <ToggleInput
        className="medium"
        label={'Deceptio AI'}
        toggled={!!model?.team?.deceptioAiEnabled}
        toggleLayout={ToggleLayout.Buttons}
        toggledLabel='On'
        notToggledLabel='Off'
        name={'deceptioAi'}
        onChange={() => model.toggleDeceptioAI()}
      />

      <div className="job-speciality-row">
        <DropdownInputControlled
          model={model.defaultJobSpeciality}
          label="Default Job Speciality"
          className="form-input job-speciality"
          // notDeselectable={true}
          showFeedback={false}
        withSearch={true}
        />
        {parseCustomSpeciality(model.defaultJobSpeciality.normValue) && model.team?.id && (
          <button
            className="specialty-info-btn"
            title="View custom specialty details"
            onClick={() => {
              const parsed = parseCustomSpeciality(model.defaultJobSpeciality.normValue);
              if (parsed && model.team?.id) {
                model.customSpecialtyDrawer.open(model.team.id, parsed.customSpecialtyId);
              }
            }}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
          </button>
        )}
      </div>
      <CustomSpecialtyDrawer model={model.customSpecialtyDrawer} />

      <DropdownInputControlled
        model={model.defaultTranslationLanguage}
        label="Default Translation Language"
        className="form-input default-translation-language"
        showFeedback={false}
        withSearch={true}
      />

      <DropdownInputControlled
        model={model.defaultTranscriptLanguage}
        label="Default Transcript Language"
        className="form-input default-transcript-language"
        showFeedback={false}
        withSearch={true}
      />

      <TeamDetailsDrawer
        className="generative-ai-drawer"
        header={'Generative AI settings'}
        description={'Select the AI provider, then enable the desired option.'}
        isExpanded={model.isCliprGptSectionExpanded}
        onExpand={() => model.toggleCliprGptSection()}>
       <GenerativeAiForm 
        disabled={false}
        model={model.generativeAiForm} />
      </TeamDetailsDrawer>

      <TeamDetailsDrawer
        className={'metadata-drawer'}
        header={'Advanced video details'}
        description={
          'Check options to customise the information you can add for library video content.'
        }
        isExpanded={model.isAdvancedSectionExpanded}
        onExpand={() => model.toggleAdvancedVideoDetails()}>
        <TeamMetadataList model={model.teamMetadata} />
      </TeamDetailsDrawer>

      <TeamDetailsDrawer
        className={'metadata-drawer'}
        header={'Dictionary'}
        isExpanded={model.isDictionaryExpanded}
        onExpand={() => model.toggleDictionary()}>
        <TeamDictionary model={model.teamDictionary} />
      </TeamDetailsDrawer>

      <TeamDetailsDrawer
        header={'Video enrichment level'}
        description={'Select your library default Video Enrichment Level.'}
        isExpanded={model.isVideoEnrichmentLevelExpanded}
        onExpand={() => model.toggleVideoEnrichmentLevel()}>
        <TeamEnrichmentLevelList model={model.teamEnrichmentLevel} />
      </TeamDetailsDrawer>

      <TeamDetailsDrawer
        header={'Embed settings'}
        description={
          'You can customise the appearance of your embedded library dashboard.'
        }
        note={
          'Note: Changing the Video Gallery Display, Theme Selection or adding a Open in Website URL will update the embed code below.'
        }
        className={'embed-settings-drawer'}
        isExpandable={true}
        isExpanded={model.isPublicTeamFormExpanded}
        onExpand={() => model.togglePublicTeamForm()}>
        <PublicTeamForm model={model.publicTeamForm} />
      </TeamDetailsDrawer>

      <TeamDetailsDrawer
        header={'Ads Settings'}
        description={
          'Add one or more VAST links for ads'
        }
        className={'ads-settings-drawer bmar4'}
        isExpandable={true}
        isExpanded={model.isAdsSettingsExpanded}
        onExpand={() => model.toggleAdsSettings()}>
        <AdsSettingsSection model={model.adsSettingsSectionState} />
      </TeamDetailsDrawer>

      <TextInput
        label="Embed Code"
        className="form-input embed-code"
        value={model.iframeEmbedCode}
        showFeedback={false}
        rows={5}
        infoModalContent={<EmbedParamsInfoPopup />}
        infoModalTitle={'Widget Configuration'}
        infoModalClassName="widget-params-window"
        multiline
        disabled
      />

      <Button
        className="secondary btn-outline small"
        label="Copy Code"
        onClick={() => model.copyIframeCode()}
      />

      <hr />

      <Button
        className="primary medium submit-btn"
        label="Save"
        onClick={() => model.submit()}
        disabled={
          !model.isChanged ||
          (model.form.hasErrorInputs && !model.form.isChangedSinceLastSubmit)
        }
      />
    </section>
  );
});
